import React, { useRef } from 'react';
import DatePicker from 'react-datepicker';
import { isValid } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Shared date picker for SafeWork360 forms.
 *
 * DEPENDENCY NOTE — do not bump react-datepicker to 9.x while this app is on
 * Create React App. 9.1.0 loads its optional `date-fns-tz` peer via
 * `require(dateFnsTzModuleName)`. Rollup/Vite ignore that, but webpack 5 cannot
 * statically analyse it and emits "Critical dependency: the request of a
 * dependency is an expression", which CRA turns into an error whenever CI=true
 * (as it is on Vercel). Installing date-fns-tz does not help, because the
 * warning is about the request being an expression, not about resolvability.
 * 8.10.0 has no timezone code at all and supports React 19. This component uses
 * only props that exist in both 8.x and 9.x, so if the app ever moves to Vite
 * the upgrade is a version bump with no call-site changes.
 *
 * Reads and writes plain ISO strings so it can be dropped straight into the
 * existing forms without touching any submission payloads:
 *   - date fields      -> 'yyyy-mm-dd'
 *   - datetime fields  -> 'yyyy-mm-ddTHH:mm'  (showTimeSelect)
 *   - year-only fields -> 'yyyy'              (yearOnly)
 *
 * Dates are displayed as dd/MM/yyyy.
 *
 * Props:
 *  - value: ISO date string (yyyy-mm-dd) or Date object
 *  - onChange: (isoString) => void by default. When `name` is supplied a
 *    synthetic event `{ target: { name, value } }` is emitted instead, so the
 *    shared `handleInputChange(e)` handlers used across the app keep working.
 *  - name: optional field name; enables the synthetic-event signature above
 *  - minDate / maxDate: ISO string or Date (strings are converted for you)
 *  - className: extra classes applied to the text input
 *  - size: 'sm' | 'lg' | undefined
 *  - placeholder: string
 *  - disabled: boolean
 *  - yearOnly: boolean — year-only picker; value/onChange use a year string
 *  - ...rest: forwarded to DatePicker (e.g. `required`)
 */

// Convert ISO string or Date to a Date object.
// iOS Safari can't parse "yyyy-mm-dd" — it needs explicit construction.
const toDate = (val, yearOnly) => {
  if (!val) return null;
  if (val instanceof Date) return isValid(val) ? val : null;
  // Year-only mode: value is a 4-digit year (number or string) -> Jan 1 of that year.
  if (yearOnly) {
    const y = typeof val === 'number' ? val : parseInt(String(val), 10);
    return Number.isFinite(y) ? new Date(y, 0, 1) : null;
  }
  // Handle yyyy-mm-dd and yyyy-mm-ddTHH:mm formats explicitly for iOS Safari
  if (typeof val === 'string') {
    const dateMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, y, m, d] = dateMatch;
      const timeMatch = val.match(/T(\d{2}):(\d{2})/);
      if (timeMatch) {
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), parseInt(timeMatch[1]), parseInt(timeMatch[2]));
      }
      return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
  }
  const d = new Date(val);
  return isValid(d) ? d : null;
};

/*
 * AdminLTE's bundled Bootstrap is imported globally after the app styles, so
 * bare class names lose ties on source order. Every rule below is scoped under
 * `.safework-datepicker` / `.safework-datepicker-popper` so it outranks both
 * AdminLTE and the pages' own `.form-group input` / `.form-group select` rules.
 */
const DATEPICKER_STYLES = `
.safework-datepicker .react-datepicker-wrapper,
.safework-datepicker .react-datepicker__input-container {
  display: block;
  width: 100%;
}
.safework-datepicker .react-datepicker__input-container input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  color: #111827;
  background: #ffffff;
  transition: all 0.2s ease;
}
.safework-datepicker .react-datepicker__input-container input::placeholder {
  color: #9ca3af;
}
.safework-datepicker .react-datepicker__input-container input:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
}
.safework-datepicker .react-datepicker__input-container input:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}
.safework-datepicker.safework-datepicker-sm .react-datepicker__input-container input {
  padding: 6px 10px;
  font-size: 13px;
}
.safework-datepicker.safework-datepicker-lg .react-datepicker__input-container input {
  padding: 12px 14px;
  font-size: 15px;
}
/* Clear button. Several pages style bare button elements, so reset it here. */
.safework-datepicker .react-datepicker__close-icon {
  display: flex;
  align-items: center;
  padding: 0 10px 0 0;
  width: auto;
  height: 100%;
  border: none;
  background: transparent;
  box-shadow: none;
}
.safework-datepicker .react-datepicker__close-icon::after {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  background-color: #9ca3af;
  color: #ffffff;
}
.safework-datepicker .react-datepicker__close-icon:hover::after {
  background-color: #6b7280;
}
/* Calendar popup. Kept above the modals, which use z-index up to 10000. */
.safework-datepicker-popper {
  z-index: 10050;
}
.safework-datepicker-popper .react-datepicker {
  font-family: inherit;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.safework-datepicker-popper .react-datepicker__header {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.safework-datepicker-popper .react-datepicker__current-month,
.safework-datepicker-popper .react-datepicker__day-name {
  color: #374151;
}
.safework-datepicker-popper .react-datepicker__month-select,
.safework-datepicker-popper .react-datepicker__year-select {
  padding: 4px 6px;
  height: auto;
  min-height: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  color: #374151;
  background: #ffffff;
  box-shadow: none;
}
.safework-datepicker-popper .react-datepicker__day:hover {
  background-color: #dcfce7;
}
.safework-datepicker-popper .react-datepicker__day--selected,
.safework-datepicker-popper .react-datepicker__day--keyboard-selected {
  background-color: #16a34a;
  color: #ffffff;
}
.safework-datepicker-popper .react-datepicker__day--selected:hover,
.safework-datepicker-popper .react-datepicker__day--keyboard-selected:hover {
  background-color: #15803d;
}
.safework-datepicker-popper .react-datepicker__day--disabled {
  color: #d1d5db;
}
.safework-datepicker-popper .react-datepicker__day--disabled:hover {
  background-color: transparent;
}
`;

const DatePickerField = ({
  value,
  onChange,
  name,
  className,
  size,
  placeholder,
  disabled,
  showTimeSelect,
  yearOnly,
  minDate,
  maxDate,
  ...rest
}) => {
  const includesTime = !!showTimeSelect && !yearOnly;

  // Chrome ignores autoComplete="off" for fields it recognises by name, so the
  // rendered input keeps a throwaway name. The `name` prop is only used for the
  // synthetic event, never for the DOM attribute.
  const inputNameRef = useRef(`dp_${Math.random().toString(36).slice(2, 7)}`);

  const emit = (nextValue) => {
    if (!onChange) return;
    if (name) {
      onChange({ target: { name, value: nextValue } });
    } else {
      onChange(nextValue);
    }
  };

  const handleChange = (date) => {
    if (!date) {
      emit('');
      return;
    }
    if (yearOnly) {
      // Return just the selected year as a string.
      emit(String(date.getFullYear()));
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    if (includesTime) {
      // Return ISO datetime string for datetime fields (yyyy-MM-ddTHH:mm)
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      emit(`${year}-${month}-${day}T${hours}:${mins}`);
    } else {
      // Return ISO date string yyyy-mm-dd
      emit(`${year}-${month}-${day}`);
    }
  };

  const sizeClass = size === 'sm' ? 'safework-datepicker-sm' : size === 'lg' ? 'safework-datepicker-lg' : '';
  const format = yearOnly ? 'yyyy' : includesTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
  const resolvedPlaceholder = placeholder || (yearOnly ? 'Select year' : 'Select date');

  return (
    <div className={`safework-datepicker ${sizeClass}`.trim()}>
      <style>{DATEPICKER_STYLES}</style>
      <DatePicker
        selected={toDate(value, yearOnly)}
        onChange={handleChange}
        dateFormat={format}
        className={className || ''}
        placeholderText={resolvedPlaceholder}
        disabled={disabled}
        autoComplete="nope"
        name={inputNameRef.current}
        minDate={toDate(minDate, yearOnly) || undefined}
        maxDate={toDate(maxDate, yearOnly) || undefined}
        showMonthDropdown={!yearOnly}
        showYearDropdown={!yearOnly}
        dropdownMode="select"
        isClearable={!disabled}
        showTimeSelect={includesTime}
        showYearPicker={yearOnly}
        popperClassName="safework-datepicker-popper"
        popperProps={{ strategy: 'fixed' }}
        {...rest}
      />
    </div>
  );
};

export default DatePickerField;
